export const useLoyaltyStore = defineStore('loyalty', () => {
  const cart = useCartStore()
  const user = useUserStore()
  const { consumer } = useApi()
  const {
    data: vouchers,
    error: vouchersError,
    status: vouchersStatus,
    pending: vouchersPending,
    execute: getVouchers
  } = consumer.getLoyaltyVouchers({ immediate: false })

  const currentReward = computed(() => {
    const currentPoints = vouchers.value?.currentLoyaltyPoints || 0
    const amount = vouchers.value?.applicableLoyaltyVouchers?.reduce((acc, voucher) => {
      return currentPoints >= voucher.points ? Number(voucher.amountInCurrency) : acc
    }, 0) || 0

    return useFormattedPrice(amount, useRuntimeConfig().public.currency[useLocale()], { minimumFractionDigits: 0 })
  })

  const reset = () => {
    vouchers.value = null
  }

  const {
    data: rewardCodes,
    pending: rewardCodesPending,
    status: rewardsCodesStatus,
    error: rewardCodesError,
    execute: getRewardCodes
  } = consumer.getPaymentInstruments({ payment_method_id: 'REWARD_CODE' }, { key: 'REWARD_CODE', immediate: false })

  const {
    data: rewardCards,
    pending: rewardCardsPending,
    status: rewardsCardsStatus,
    error: rewardCardsError,
    execute: getRewardCards
  } = consumer.getPaymentInstruments({ payment_method_id: 'REWARD_CARD' }, {
    immediate: false,
    transform: (rewards) => rewards.map(({ card }) => card)
  })

  const getRewards = async () => Promise.all([getRewardCodes(), getRewardCards()])

  const rewardsAvailable = computed(() => [
    ...rewardCodes.value || [],
    ...rewardCards.value || []
  ])

  const rewardsPending = computed(() => rewardCodesPending.value || rewardCardsPending.value)

  const rewardsError = computed(() => rewardCodesError.value || rewardCardsError.value)

  const rewardsRequested = computed(() => rewardsCodesStatus.value !== 'idle' && rewardsCardsStatus.value !== 'idle')

  const athletePayment = computed(() => {
    const athleteMethod = cart.paymentMethods.find(({ code }) => code === 'ATHLETES_PAYMENT')

    const remainingAmount = athleteMethod?.additionalData?.remainingAmount ?? 0
    const payableAmount = athleteMethod?.additionalData?.payableAmount ?? 0

    return user.isAthleteProfile ? { remainingAmount, payableAmount } : undefined
  })

  const creditedConsumerPayment = computed(() => {
    if (!user.creditedConsumerProfileType) return undefined

    const method = cart.paymentMethods.find(({ code }) => code === 'INFLUENCER_CREDIT')

    if (!method) return undefined

    return {
      ...method?.additionalData,
      remainingAmount: method?.additionalData?.remainingAmount ?? 0,
      payableAmount: method?.additionalData?.payableAmount ?? 0,
    }
  })

  const rewardsPaymentAllowed = computed(() => {
    const athleteBlocked = athletePayment.value && athletePayment.value.remainingAmount > 0
    const creditedConsumerBlocked = creditedConsumerPayment.value && creditedConsumerPayment.value.remainingAmount > 0
    return !(athleteBlocked || creditedConsumerBlocked)
  })

  return {
    athletePayment,
    creditedConsumerPayment,
    currentReward,
    getVouchers,
    getRewards,
    reset,
    rewardsAvailable,
    rewardsError,
    rewardsPaymentAllowed,
    rewardsPending,
    rewardsRequested,
    vouchers,
    vouchersError,
    vouchersStatus,
    vouchersPending
  }
})
